import React from "react";
import { HtmlHTMLAttributes, MouseEvent } from "react";

import { DateTime } from "luxon";

import "./Calendar.css";

interface ICalendarProps extends HtmlHTMLAttributes<HTMLElement> {
    year: number;
    month: number;
    highlightDate?: Date;
    events?: { [day: number]: any[] };
    onDaySelected?: (date: Date) => void;
    onDisplayRangeChanged?: (minDate: Date, maxDate: Date) => void;
}

class Calendar extends React.Component<ICalendarProps> {
    constructor(props: Readonly<ICalendarProps>) {
        super(props);

        this.handleDayClick = this.handleDayClick.bind(this);

        this.calculateDisplayedRange();
    }

    public componentDidUpdate(prevProps: Readonly<ICalendarProps>) {
        if (this.props.month !== prevProps.month || this.props.year !== prevProps.year) {
            this.calculateDisplayedRange();
        }
    }

    public render() {
        if (!this.props.year || isNaN(this.props.year) || !this.props.month || isNaN(this.props.month)) {
            return (
                <div className={"calendar " + this.props.className}>
                    <header>
                        <div className="day weekend">Sun</div>
                        <div className="day">Mon</div>
                        <div className="day">Tues</div>
                        <div className="day">Wed</div>
                        <div className="day">Thu</div>
                        <div className="day">Fri</div>
                        <div className="day weekend">Sat</div>
                    </header>
                    <div className="calendar-inner" />
                </div>
            );
        }

        const month = new Date(this.props.year, this.props.month - 1, 1);
        const dayOffset = month.getDay(); // Day of week
        const daysInMonth = new Date(this.props.year, this.props.month, 0).getDate();
        const dayHighlight = this.props.highlightDate ? this.props.highlightDate.getDate() : 0;
        const isHighlightInMonth = this.props.highlightDate ? (this.props.highlightDate.getMonth() + 1 === this.props.month && this.props.highlightDate.getFullYear() === this.props.year) : false;

        // One row per week
        const mainOut = [];
        let weekOut = [];
        for (let i = 0; i <= 42 /* 6 weeks display */; i++) {
            if (i > 0 && (i % 7) === 0) {
                mainOut.push(<div key={i / 7} className="week">{weekOut}</div>);
                weekOut = [];

                if (i === 42) {
                    break;
                }
            }

            const monthDay = (i - dayOffset) + 1;
            const isMonthDay = monthDay > 0 && monthDay <= daysInMonth;
            const dayEvents = (this.props.events) ? this.props.events[monthDay] : null;
            month.setMonth(this.props.month - 1, monthDay);
            const monthDayDisplay = month.getDate();

            weekOut.push((
                <div key={monthDay} data-date={monthDay} onClick={this.handleDayClick} className={"day" + ((isHighlightInMonth && ((i - dayOffset) + 1) === dayHighlight) ? " today" : "") + (!isMonthDay ? " spacer" : "")}>
                    <span>{monthDayDisplay}</span>
                    <div className="cal-event-list">
                        {dayEvents ? dayEvents.map(e => <div key={e.id || e.title} className={`cal-event ${e.type ? `cal-event-${e.type}` : ""}`}>{e.title}</div>) : ""}
                    </div>
                </div>
            ));
        }

        return (
            <div className={"calendar " + this.props.className}>
                <header>
                    {/* <div className="day">Week</div> */}
                    <div className="day weekend">Sun</div>
                    <div className="day">Mon</div>
                    <div className="day">Tues</div>
                    <div className="day">Wed</div>
                    <div className="day">Thu</div>
                    <div className="day">Fri</div>
                    <div className="day weekend">Sat</div>
                </header>
                <div className="calendar-inner">
                    {mainOut}
                </div>
            </div>
        );
    }

    private handleDayClick(e: MouseEvent<HTMLDivElement>) {
        if (!this.props.onDaySelected) {
            return;
        }

        const dateAttr = e.currentTarget.attributes.getNamedItem("data-date");
        if (!dateAttr) {
            return;
        }

        const date = new Date();
        date.setHours(0, 0, 0, 0);
        date.setFullYear(this.props.year, this.props.month - 1, parseInt(dateAttr.value, 10));

        this.props.onDaySelected(date);
    }

    private calculateDisplayedRange() {
        if (!this.props.year || isNaN(this.props.year) || !this.props.month || isNaN(this.props.month)) {
            return;
        }

        const month = DateTime.local(this.props.year, this.props.month);
        const dayOffset = month.weekday % 7; // Day of week
        const daysInMonth = month.daysInMonth;
        const extraDays = 42 - dayOffset - daysInMonth;

        const minDate = month.plus({ days: -dayOffset });
        const maxDate = month.endOf("month").plus({ days: extraDays });

        if (this.props.onDisplayRangeChanged) {
            this.props.onDisplayRangeChanged(minDate.toJSDate(), maxDate.toJSDate());
        }
    }
}

export default Calendar;
